// Map components shared by the DMS and the citizen portal. The map engine (MapLibre, ~250 kB gzip) is a separate bundle,
// /maps/map.js, loaded the first time a map is shown.
import React, { useEffect, useRef, useState } from 'react';
import type { DocFlowMapApi, GisConfig } from './map';

/** The value of a LOCATION attribute (documents, portal requests, urbanism dossiers). */
export interface LocationValue {
  lat: number; lon: number;
  address?: string | null; street?: string | null; housenumber?: string | null; locality?: string | null; uat?: string | null; county?: string | null; postcode?: string | null;
  approximate?: boolean; insideArea?: boolean | null; source?: 'search' | 'map' | 'gps' | 'import';
}
export interface Place { id?: number; kind: string; label: string; lat: number; lon: number; street?: string; housenumber?: string; locality?: string; uat?: string; county?: string; postcode?: string; approximate?: boolean; insideArea?: boolean; bbox?: [number, number, number, number] }

let loading: Promise<DocFlowMapApi> | null = null;
/** Loads /maps/map.js and /maps/map.css once. */
export function loadMaps(): Promise<DocFlowMapApi> {
  const w = window as any;
  if (w.DocFlowMap) return Promise.resolve(w.DocFlowMap);
  if (!loading) loading = new Promise((res, rej) => {
    const css = document.createElement('link'); css.rel = 'stylesheet'; css.href = '/maps/map.css?v=' + MAPS_V; document.head.appendChild(css);
    const s = document.createElement('script'); s.src = '/maps/map.js?v=' + MAPS_V; s.async = true;
    s.onload = () => w.DocFlowMap ? res(w.DocFlowMap) : rej(new Error('Harta nu s-a încărcat'));
    s.onerror = () => { loading = null; rej(new Error('Harta nu s-a putut încărca')); };
    document.head.appendChild(s);
  });
  return loading;
}
declare const __MAPS_V__: string;
const MAPS_V = typeof __MAPS_V__ === 'string' ? __MAPS_V__ : '1';

export async function searchPlaces(q: string, limit = 8): Promise<Place[]> {
  const r = await fetch('/api/gis/public/search?' + new URLSearchParams({ q, limit: String(limit) }));
  if (!r.ok) throw new Error(r.status === 429 ? 'Prea multe căutări, reîncercați imediat' : 'Căutarea nu este disponibilă');
  return r.json();
}
export async function reversePlace(lat: number, lon: number): Promise<Place> {
  const r = await fetch('/api/gis/public/reverse?' + new URLSearchParams({ lat: lat.toFixed(6), lon: lon.toFixed(6) }));
  if (!r.ok) { const j = await r.json().catch(() => null); throw new Error(j?.error || 'Adresa nu a putut fi găsită'); }
  return r.json();
}
export function fromPlace(p: Place, source: LocationValue['source']): LocationValue {
  return { lat: round(p.lat), lon: round(p.lon), address: p.label, street: p.street || null, housenumber: p.housenumber || null, locality: p.locality || null, uat: p.uat || null,
    county: p.county || null, postcode: p.postcode || null, approximate: !!p.approximate || p.kind === 'point', insideArea: p.insideArea ?? null, source };
}
const round = (v: number) => Math.round(v * 1e6) / 1e6;
export function isLocation(v: any): v is LocationValue { return v && typeof v === 'object' && typeof v.lat === 'number' && typeof v.lon === 'number'; }
export function locationText(v: any): string {
  if (!isLocation(v)) return '';
  return v.address || `${v.lat.toFixed(5)}, ${v.lon.toFixed(5)}`;
}

/** A map in a box; `onReady` receives the MapLibre map once the base map is loaded. */
export function MapBox({ height = 300, onReady, center, zoom, interactive, area, className, children }: {
  height?: number | string; onReady?: (map: any, cfg: GisConfig, lib: DocFlowMapApi) => void | (() => void); center?: [number, number]; zoom?: number; interactive?: boolean; area?: boolean; className?: string; children?: React.ReactNode;
}) {
  const el = useRef<HTMLDivElement>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let map: any = null, cleanup: void | (() => void), dead = false;
    loadMaps().then(lib => lib.create(el.current!, { center, zoom, interactive, area }).then(({ map: m, cfg }) => {
      if (dead) { m.remove(); return; }
      map = m; cleanup = onReady?.(m, cfg, lib);
    })).catch(e => !dead && setError(e.message || String(e)));
    return () => { dead = true; if (typeof cleanup === 'function') cleanup(); if (map) map.remove(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className={'dfmap ' + (className || '')} style={{ height }}>
      <div ref={el} className="dfmap-canvas" />
      {error && <div className="dfmap-error">{error}</div>}
      {children}
    </div>
  );
}

/**
 * Picks a point: type an address (suggestions from the server's address index), click on the map or drag the pin, or use the
 * device's position. The nearest known address is filled in and can be corrected by hand.
 */
export function LocationPicker({ value, onChange, disabled, height = 300, required, compact }: { value: LocationValue | null | undefined; onChange: (v: LocationValue | null) => void; disabled?: boolean; height?: number; required?: boolean; compact?: boolean }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<Place[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const mapRef = useRef<any>(null), markerRef = useRef<any>(null), libRef = useRef<DocFlowMapApi | null>(null);
  const valueRef = useRef(value); valueRef.current = value;
  const changeRef = useRef(onChange); changeRef.current = onChange;

  // suggestions while typing
  useEffect(() => {
    if (q.trim().length < 3) { setResults([]); return; }
    const t = setTimeout(() => { searchPlaces(q, 8).then(r => { setResults(r); setOpen(true); setMsg(r.length ? '' : 'Nicio adresă găsită — alegeți punctul pe hartă.'); }).catch(e => setMsg(e.message)); }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const place = (v: LocationValue | null, fly = true) => {
    const map = mapRef.current, lib = libRef.current;
    if (!map || !lib) return;
    if (!v) { markerRef.current?.remove(); markerRef.current = null; return; }
    if (!markerRef.current) {
      markerRef.current = lib.marker('#d9480f', !disabled).setLngLat([v.lon, v.lat]).addTo(map);
      markerRef.current.on('dragend', () => { const p = markerRef.current.getLngLat(); pick(p.lat, p.lng, 'map'); });
    } else markerRef.current.setLngLat([v.lon, v.lat]);
    if (fly) map.flyTo({ center: [v.lon, v.lat], zoom: Math.max(map.getZoom(), 16), duration: 600 });
  };
  const pick = async (lat: number, lon: number, source: LocationValue['source']) => {
    const base: LocationValue = { lat: round(lat), lon: round(lon), source, address: null, approximate: true };
    place(base, false);
    setBusy(true);
    try { const p = await reversePlace(lat, lon); changeRef.current({ ...fromPlace(p, source), lat: round(lat), lon: round(lon), approximate: p.kind !== 'house' || (p as any).distance > 25 }); setMsg(''); }
    catch (e: any) { changeRef.current(base); setMsg(e.message); }
    finally { setBusy(false); }
  };
  const choose = (p: Place) => { setOpen(false); setQ(''); const v = fromPlace(p, 'search'); onChange(v); place(v); };
  const gps = () => {
    if (!navigator.geolocation) { setMsg('Dispozitivul nu oferă localizarea'); return; }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(pos => { pick(pos.coords.latitude, pos.coords.longitude, 'gps'); mapRef.current?.flyTo({ center: [pos.coords.longitude, pos.coords.latitude], zoom: 17 }); },
      () => { setBusy(false); setMsg('Localizarea nu a fost permisă'); }, { enableHighAccuracy: true, timeout: 10000 });
  };
  useEffect(() => { place(value ?? null, false); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [value?.lat, value?.lon]);

  return (
    <div className={'dfpick ' + (compact ? 'compact' : '')}>
      {!disabled && (
        <div className="dfpick-search">
          <input className="dfpick-input" placeholder="Căutați adresa: strada, numărul, localitatea…" value={q} onChange={e => setQ(e.target.value)}
            onFocus={() => results.length && setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 180)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (results[0]) choose(results[0]); } if (e.key === 'Escape') setOpen(false); }} />
          <button type="button" className="dfpick-btn" onClick={gps} title="Folosește poziția dispozitivului">⌖ Locația mea</button>
          {open && results.length > 0 && (
            <div className="dfpick-results">
              {results.map((r, i) => (
                <button type="button" key={(r.id ?? i) + r.label} onMouseDown={e => { e.preventDefault(); choose(r); }}>
                  <span className={'dfpick-kind k-' + r.kind}>{r.kind === 'house' ? 'adresă' : r.kind === 'street' ? 'stradă' : 'localitate'}</span>
                  <span>{r.label}</span>{r.approximate && <em>număr aproximat pe stradă</em>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <MapBox height={height} center={value ? [value.lon, value.lat] : undefined} zoom={value ? 17 : undefined}
        onReady={(map, _cfg, lib) => {
          mapRef.current = map; libRef.current = lib;
          if (valueRef.current) place(valueRef.current, false);
          if (!disabled) { map.getCanvas().style.cursor = 'crosshair'; map.on('click', (e: any) => pick(e.lngLat.lat, e.lngLat.lng, 'map')); }
          return () => { mapRef.current = null; markerRef.current = null; };
        }} />
      <div className="dfpick-foot">
        {value ? (
          <>
            {disabled ? <span className="dfpick-addr">{locationText(value)}</span>
              : <input className="dfpick-input addr" value={value.address || ''} placeholder="Adresa (completați sau corectați)" onChange={e => onChange({ ...value, address: e.target.value })} />}
            <span className="dfpick-meta">{value.lat.toFixed(6)}, {value.lon.toFixed(6)}{value.approximate ? ' · aproximativ' : ''}</span>
            {value.insideArea === false && <span className="dfpick-warn">în afara teritoriului instituției</span>}
            {!disabled && <button type="button" className="dfpick-btn ghost" onClick={() => { onChange(null); place(null); }}>Șterge</button>}
          </>
        ) : <span className="dfpick-meta">{busy ? 'Se caută adresa…' : disabled ? 'Fără locație' : (required ? 'Obligatoriu: ' : '') + 'căutați adresa sau faceți clic pe hartă.'}</span>}
        {msg && <span className="dfpick-warn">{msg}</span>}
      </div>
    </div>
  );
}

/** A small read-only map with the pin, and the address under it. */
export function LocationView({ value, height = 200 }: { value: LocationValue; height?: number }) {
  return (
    <div className="dfpick compact">
      <MapBox height={height} center={[value.lon, value.lat]} zoom={16.5} area={false} onReady={(map, _c, lib) => { lib.marker('#d9480f').setLngLat([value.lon, value.lat]).addTo(map); }} />
      <div className="dfpick-foot"><span className="dfpick-addr">{locationText(value)}</span><span className="dfpick-meta">{value.lat.toFixed(6)}, {value.lon.toFixed(6)}{value.approximate ? ' · aproximativ' : ''}</span></div>
    </div>
  );
}
